import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuditConfigTab = () => {
  const [auditSettings, setAuditSettings] = useState({
    auditFrequency: "quarterly",
    retentionPeriod: "7",
    retentionUnit: "years",
    autoBackup: true,
    backupFrequency: "daily",
    complianceStandard: "sox",
    requireApproval: true,
    auditTrailEnabled: true,
    encryptionEnabled: true,
    anonymizeData: false
  });

  const [complianceTemplates, setComplianceTemplates] = useState([
    {
      id: 1,
      name: "SOX Compliance Template",
      standard: "SOX",
      description: "Sarbanes-Oxley Act compliance requirements for financial reporting",
      requirements: ["Asset valuation tracking", "Change management logs", "Access control records"],
      isActive: true,
      lastUpdated: "2025-08-15"
    },
    {
      id: 2,
      name: "GDPR Data Protection",
      standard: "GDPR",
      description: "General Data Protection Regulation compliance for personal data",
      requirements: ["Data retention policies", "User consent tracking", "Data anonymization"],
      isActive: true,
      lastUpdated: "2025-08-10"
    },
    {
      id: 3,
      name: "ISO 27001 Security",
      standard: "ISO 27001",
      description: "Information security management system requirements",
      requirements: ["Security incident logs", "Asset classification", "Risk assessments"],
      isActive: false,
      lastUpdated: "2025-07-20"
    }
  ]);

  const [backupStatus, setBackupStatus] = useState({
    lastBackup: "2025-08-19T04:00:00Z",
    backupSize: "2.4 GB",
    status: "success",
    nextScheduled: "2025-08-20T04:00:00Z"
  });

  const [isModified, setIsModified] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    standard: '',
    description: '',
    requirements: [],
  });
  const [newRequirement, setNewRequirement] = useState('');

  const auditFrequencyOptions = [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "semi_annual", label: "Semi-Annual" },
    { value: "annual", label: "Annual" }
  ];

  const retentionUnitOptions = [
    { value: "months", label: "Months" },
    { value: "years", label: "Years" }
  ];

  const backupFrequencyOptions = [
    { value: "hourly", label: "Every Hour" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
  ];

  const complianceStandardOptions = [
    { value: "sox", label: "SOX (Sarbanes-Oxley)" },
    { value: "gdpr", label: "GDPR (Data Protection)" },
    { value: "iso27001", label: "ISO 27001 (Security)" },
    { value: "hipaa", label: "HIPAA (Healthcare)" },
    { value: "custom", label: "Custom Standard" }
  ];

  const fetchAuditConfigurations = async () => {
    try {
      const response = await axios.get('/audit-configurations');

      if (response.data.success) {
        const config = response.data.configuration;

        setAuditSettings({
          auditFrequency: config.audit_frequency,
          retentionPeriod: config.retention_period,
          retentionUnit: config.retention_unit,
          autoBackup: config.auto_backup,
          backupFrequency: config.backup_frequency,
          complianceStandard: config.compliance_standard,
          requireApproval: config.require_approval,
          auditTrailEnabled: config.audit_trail_enabled,
          encryptionEnabled: config.encryption_enabled,
          anonymizeData: config.anonymize_data,
        });

        setComplianceTemplates(response.data.templates);
        setBackupStatus({
          lastBackup: config.last_backup,
          backupSize: config.backup_size,
          status: config.backup_status,
          nextScheduled: config.next_scheduled,
        });
      }
    } catch (error) {
      console.error('Error fetching audit configurations:', error);
    }
  };

  useEffect(() => {
    fetchAuditConfigurations();
  }, []);

  const handleSettingChange = (field, value) => {
    setAuditSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const handleSaveSettings = async () => {
    try {
      const response = await axios.post('/audit-configurations', {
        audit_frequency: auditSettings.auditFrequency,
        retention_period: auditSettings.retentionPeriod,
        retention_unit: auditSettings.retentionUnit,
        auto_backup: auditSettings.autoBackup,
        backup_frequency: auditSettings.backupFrequency,
        compliance_standard: auditSettings.complianceStandard,
        require_approval: auditSettings.requireApproval,
        audit_trail_enabled: auditSettings.auditTrailEnabled,
        encryption_enabled: auditSettings.encryptionEnabled,
        anonymize_data: auditSettings.anonymizeData,
      });

      if (response.data.success) {
        setIsModified(false);
        alert(response.data.message || 'Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    }
  };

  const handleRunBackup = async () => {
    try {
      setBackupStatus(prev => ({
        ...prev,
        status: 'running'
      }));

      const response = await axios.post('/audit-configurations/backup');

      if (response.data.success) {
        await fetchAuditConfigurations();
        alert(response.data.message || 'Backup completed successfully');
      }
    } catch (error) {
      console.error('Error running backup:', error);
      setBackupStatus(prev => ({
        ...prev,
        status: 'error'
      }));
      alert('Backup failed');
    }
  };

  const handleToggleTemplate = async (id) => {
    try {
      const response = await axios.post(`/audit-configurations/template-toggle/${id}`);

      if (response.data.success) {
        await fetchAuditConfigurations();
      }
    } catch (error) {
      console.error('Error toggling template:', error);
      alert('Failed to update template status');
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) {
      return 'N/A';
    }
    return new Date(timestamp).toLocaleString();
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setNewTemplate(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (indexToRemove) => {
    setNewTemplate(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, index) => index !== indexToRemove)
    }));
  };

  const getBackupStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'running':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getBackupStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'CheckCircle';
      case 'running':
        return 'Clock';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Clock';
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.standard) {
      alert('Please fill in template name and standard');
      return;
    }

    try {
      const response = await axios.post('/audit-configurations/template', {
        name: newTemplate.name,
        standard: newTemplate.standard,
        description: newTemplate.description,
        requirements: newTemplate.requirements,
      });

      if (response.data.success) {
        await fetchAuditConfigurations();
        setShowTemplateModal(false);
        setNewTemplate({
          name: '',
          standard: '',
          description: '',
          requirements: [],
        });
        setNewRequirement('');
        alert('Template created successfully');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template');
    }
  };

  const handleResetChanges = () => {
    setAuditSettings({
      auditFrequency: "quarterly",
      retentionPeriod: "7",
      retentionUnit: "years",
      autoBackup: true,
      backupFrequency: "daily",
      complianceStandard: "sox",
      requireApproval: true,
      auditTrailEnabled: true,
      encryptionEnabled: true,
      anonymizeData: false
    });
    setIsModified(false);
  };

  return (
    <div className="space-y-8">
      {/* Audit Schedule Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Calendar" size={20} className="text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Audit Schedule Configuration</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Audit Frequency"
            description="How often should audits be conducted"
            options={auditFrequencyOptions}
            value={auditSettings.auditFrequency}
            onChange={(value) => handleSettingChange('auditFrequency', value)}
          />
          
          <div className="flex space-x-2">
            <Input
              label="Data Retention Period"
              type="number"
              value={auditSettings.retentionPeriod}
              onChange={(e) => handleSettingChange('retentionPeriod', e.target.value)}
              className="flex-1"
            />
            <Select
              label="Unit"
              options={retentionUnitOptions}
              value={auditSettings.retentionUnit}
              onChange={(value) => handleSettingChange('retentionUnit', value)}
              className="w-32"
            />
          </div>
          
          <Select
            label="Compliance Standard"
            options={complianceStandardOptions}
            value={auditSettings.complianceStandard}
            onChange={(value) => handleSettingChange('complianceStandard', value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="space-y-4">
            <Checkbox
              label="Require Approval for Changes"
              description="All audit configuration changes need administrator approval"
              checked={auditSettings.requireApproval}
              onChange={(e) => handleSettingChange('requireApproval', e.target.checked)}
            />
            
            <Checkbox
              label="Enable Audit Trail"
              description="Track all user actions and system changes"
              checked={auditSettings.auditTrailEnabled}
              onChange={(e) => handleSettingChange('auditTrailEnabled', e.target.checked)}
            />
          </div>
          
          <div className="space-y-4">
            <Checkbox
              label="Enable Data Encryption"
              description="Encrypt audit logs and backup files"
              checked={auditSettings.encryptionEnabled}
              onChange={(e) => handleSettingChange('encryptionEnabled', e.target.checked)}
            />
            
            <Checkbox
              label="Anonymize Personal Data"
              description="Remove personally identifiable information from logs"
              checked={auditSettings.anonymizeData}
              onChange={(e) => handleSettingChange('anonymizeData', e.target.checked)}
            />
          </div>
        </div>
      </div>

      {/* Backup Configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Database" size={20} className="text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Backup Configuration</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable Automatic Backup"
              description="Automatically backup audit data and configurations"
              checked={auditSettings.autoBackup}
              onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
            />
            
            <Select
              label="Backup Frequency"
              options={backupFrequencyOptions}
              value={auditSettings.backupFrequency}
              onChange={(value) => handleSettingChange('backupFrequency', value)}
              disabled={!auditSettings.autoBackup}
            />
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Backup Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Last Backup:</span>
                <div className={`flex items-center space-x-1 ${getBackupStatusColor(backupStatus.status)}`}>
                  <Icon name={getBackupStatusIcon(backupStatus.status)} size={14} />
                  <span>{formatDateTime(backupStatus.lastBackup)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Backup Size:</span>
                <span className="font-medium text-gray-900">{backupStatus.backupSize}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Next Scheduled:</span>
                <span className="font-medium text-gray-900">{formatDateTime(backupStatus.nextScheduled)}</span>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunBackup}
              disabled={backupStatus.status === 'running'}
              loading={backupStatus.status === 'running'}
              iconName="Download"
              iconPosition="left"
              className="w-full mt-4"
            >
              Run Manual Backup
            </Button>
          </div>
        </div>
      </div>

      {/* Compliance Templates */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Compliance Templates</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowTemplateModal(true)}
          >
            Add Template
          </Button>
        </div>
        
        <div className="space-y-4">
          {complianceTemplates.map((template) => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${
                    template.isActive ? 'bg-blue-600' : 'bg-gray-500'
                  }`}>
                    {template.standard.substring(0, 2)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    template.isActive 
                      ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {template.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2">Requirements:</p>
                <div className="flex flex-wrap gap-1">
                  {template.requirements.map((req, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                    >
                      {req}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  Last updated: {template.lastUpdated}
                </span>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={template.isActive}
                    onChange={() => handleToggleTemplate(template.id)}
                    label="Enabled"
                  />
                  <Button variant="ghost" size="sm" iconName="Edit" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Icon name="Clock" size={16} />
          <span>Configuration last updated: Aug 19, 2025 at 4:36 AM</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleResetChanges}
            disabled={!isModified}
          >
            Reset Changes
          </Button>
          <Button
            variant="default"
            onClick={handleSaveSettings}
            disabled={!isModified}
            iconName="Save"
            iconPosition="left"
          >
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Add Compliance Template</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowTemplateModal(false)}
              >
                <Icon name="X" size={18} />
              </Button>
            </div>

            <div className="space-y-4">
              <Input
                label="Template Name"
                placeholder="Enter template name"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
              />

              <Input
                label="Standard"
                placeholder="Enter compliance standard (e.g., SOX, GDPR)"
                value={newTemplate.standard}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, standard: e.target.value }))}
              />

              <Input
                label="Description"
                placeholder="Enter template description"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a requirement"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addRequirement();
                      }
                    }}
                  />
                  <Button type="button" onClick={addRequirement}>
                    Add
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50 rounded-lg">
                {newTemplate.requirements.length === 0 ? (
                  <p className="text-sm text-gray-500">No requirements added yet</p>
                ) : (
                  newTemplate.requirements.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        className="hover:text-blue-900 ml-1"
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowTemplateModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateTemplate}
                disabled={!newTemplate.name || !newTemplate.standard}
              >
                Create Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditConfigTab;